import React from 'react';
import { Box, Tab, Tabs, styled } from '@mui/material';
import Color from 'color';
import { CardWrapper } from '../../../../../../agencies/quotes/PrimeX/requestQuote/components/BaseHeader';
import { Show } from '../../../../../../../components/Show';
import UwRaterOptions from './header/UwRaterOptions';
import UwQuoteList from './header/UwQuoteList';
import { useUnderwriterContext } from '../provider/UnderwriterProvider';
import {
  UW_GRID_INSET_LEFT,
  UW_GRID_INSET_RIGHT,
  UW_HEADER_CONTROLS_END,
  UW_TILE_CARD_STYLES,
  UW_TILE_GAP,
  UW_TILE_PADDING_Y,
} from './uwTileSpacing';
import { isCoverageEndorsementsState, isUnderwriterContext } from '../types';
import GlobalComponentFactory from '../../../components/global/GlobalComponentFactory';
import UwPreferencesMenu from './header/UwPreferencesMenu';
import type { UwPreferences } from '../hooks/useUwPreferences';

interface UnderwriterGlobalHeaderProps {
  children?: React.ReactNode;
  preferences?: UwPreferences;
  onPreferenceToggle?: <K extends keyof UwPreferences>(
    key: K,
    value: UwPreferences[K]
  ) => void;
}

const UnderwriterGlobalHeader = ({
  children,
  preferences,
  onPreferenceToggle,
}: UnderwriterGlobalHeaderProps) => {
  const context = useUnderwriterContext();
  const isUwQuoteState = isUnderwriterContext(context);
  const shouldShowGlobalControls =
    isUwQuoteState || isCoverageEndorsementsState(context.state);
  const [selectedTab, setSelectedTab] = React.useState<SubmissionTabs>(
    SUBMISSION_TAB_NAMES.RATER_OPTIONS
  );

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue as SubmissionTabs);
  };

  return (
    <CardWrapper
      style={{ position: 'relative', marginBottom: UW_TILE_GAP }}
      // No side padding of its own: the tab row and the globals row each pad
      // to the edges they line up with.
      cardStyles={{ ...UW_TILE_CARD_STYLES, paddingLeft: 0, paddingRight: 0 }}
      boxProps={{ display: 'block', py: UW_TILE_PADDING_Y, px: 0 }}
    >
      {shouldShowGlobalControls ? (
        <Box sx={{ width: '100%' }}>
          {!children ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                pl: UW_GRID_INSET_LEFT,
                pr: UW_HEADER_CONTROLS_END,
                // Flush with the card's top edge; the tabs' own padding
                // supplies the tile's top spacing.
                marginTop: `calc(-1 * ${UW_TILE_PADDING_Y})`,
                borderBottom: 1,
                borderColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? Color(theme.palette.divider).lighten(0.35).toString()
                    : theme.palette.divider,
              }}
            >
              <StyledTabs value={selectedTab} onChange={handleChange}>
                <StyledTab label="Rater Options" />
                <Show
                  when={isUwQuoteState && context.state.quoteList?.length > 1}
                >
                  {/* The count is every entry in the dropdown this opens,
                      the current quote included. */}
                  <StyledTab
                    label={`Related Quotes (${
                      isUwQuoteState ? context.state.quoteList?.length : 0
                    })`}
                  />
                </Show>
              </StyledTabs>

              {preferences && onPreferenceToggle ? (
                <Box sx={{ pb: 0.5 }}>
                  <UwPreferencesMenu
                    preferences={preferences}
                    onToggle={onPreferenceToggle}
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}

          {/* Padded to the coverage grid's edges. */}
          <Box pt={1} pl={UW_GRID_INSET_LEFT} pr={UW_GRID_INSET_RIGHT}>
            <GlobalComponentFactory
              leading={
                children ?? (
                  <>
                    <Show
                      when={selectedTab === SUBMISSION_TAB_NAMES.RATER_OPTIONS}
                    >
                      <UwRaterOptions />
                    </Show>
                    <Show
                      when={selectedTab === SUBMISSION_TAB_NAMES.RELATED_QUOTES}
                    >
                      <UwQuoteList />
                    </Show>
                  </>
                )
              }
            />
          </Box>
        </Box>
      ) : (
        children
      )}
    </CardWrapper>
  );
};

export default UnderwriterGlobalHeader;

const SUBMISSION_TAB_NAMES = {
  RATER_OPTIONS: 0,
  RELATED_QUOTES: 1,
} as const;

type SubmissionTabs =
  (typeof SUBMISSION_TAB_NAMES)[keyof typeof SUBMISSION_TAB_NAMES];

/* Tabs */
interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{
      children: <span className="MuiTabs-indicatorSpan" />,
    }}
  />
))(({ theme }) => {
  return {
    minHeight: 'auto',
    '& .MuiTabs-indicator': {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      height: 2,
      bottom: -1,
      zIndex: 1,
    },
    '& .MuiTabs-indicatorSpan': {
      maxWidth: '100%',
      width: '100%',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.config.colors.cowbellBlue
          : Color(theme.config.colors.cowbellBlue).lighten(0.15).toString(),
    },
  };
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: '1.15rem',
  marginRight: '0.35rem',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : 'rgba(0, 0, 0, 0.7)',
  // MUI's 48px minimum centred the label well below the card edge; the
  // padding alone now places it, at the tile's own spacing.
  minHeight: 'auto',
  padding: `${UW_TILE_PADDING_Y} 0.5rem`,
  '&.Mui-selected': {
    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.config.colors.cowbellBlue,
  },
}));
